import { startOfYear, endOfYear } from "date-fns";
import { format, parseISO } from "date-fns";
import { atom } from "jotai";
import { sv } from "date-fns/locale";
import { toast } from "react-toastify";

export function debounce(callback, wait) {
  let timeout;
  return (...args) => {
    const context = this;
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      callback.apply(context, args);
    }, wait);
  };
}

export function tailwindColor(name) {
  // Not super clean way of getting tailwind colors. TODO improve this.
  let el = document.createElement("div");
  el.classList.add(name);
  document.body.appendChild(el);
  let color = getComputedStyle(el).backgroundColor;
  document.body.removeChild(el);

  return color;
}

export const siteColors = {
  primary: tailwindColor("bg-primary"),
  secondary: tailwindColor("bg-apricot"),
  tertiary: tailwindColor("bg-tertiary"),
  primaryLight: tailwindColor("bg-primary-light"),
  border: tailwindColor("borderColor"),
};

export function stringToInitials(str, length = 2) {
  if (!str) {
    return str;
  }
  return str
    .match(/\b(\w)/g)
    .slice(0, length)
    .map((char) => char.toUpperCase());
}

export default class SwedishOrgNr {
  static validate(orgnr) {
    if (orgnr) {
      const orgnumber = orgnr.replace(/\D/g, "");

      if (
        orgnumber.length < 10 ||
        orgnumber.length > 12 ||
        orgnumber.length === 11
      ) {
        return false;
      }

      return isValidLuhn(orgnumber);
    }

    return false;
  }
}

/**
 * Check if last digit of number is vald Luhn control digit
 * @param pnr
 * @returns {boolean}
 **/
function isValidLuhn(pnr) {
  let number;
  let checksum = 0;

  for (var i = pnr.length - 1; i >= 0; i--) {
    number = parseInt(pnr.charAt(i));

    if (i % 2 == 1) {
      checksum += number;
    } else {
      checksum += number * 2 > 9 ? number * 2 - 9 : number * 2;
    }
  }

  return checksum % 10 == 0;
}

export const isEmail = (val) => {
  return val.match(
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
  );
};

/**
 * Negative and Positive numbers
 *
 * This number is usually called signed float or signed double, but you can also call it a fractional number. This includes numbers like 0, 1, 0.0, 0.1, 1.0, 99999.000001, 5.10, -0, -1, -0.0, -0.1, -99999.000001, 5.10.
 * The Regular Expression that covers this validation is:
 *
 * @param number
 * @returns boolean
 */
export const isValidNumber = (number) => {
  if (number === null) {
    return false;
  }
  return !!number.toString().match(/^-?(0|[1-9]\d*)(\.\d+)?$/);
};

/**
 *  Convert number to a more human readable format, for example 1000 kg to 1 ton
 *
 * @returns number
 * @param number
 * @param round
 */
export const convertToHumanReadable = (number, round = true) => {
  return Intl.NumberFormat("sv-SE").format(round ? Math.round(number) : number);
};

/**
 * Convert weight to readable format and return the unit
 *
 * @param weight
 * @param shortForm - if true, return t instead of tonnes
 * @returns {{unit: (string), weight: *, concat: (string)}}
 */
export const convertWeightToHumanReadable = (weight, shortForm = false) => {
  let showInTonnes = weight > 1000 || weight < -1000;

  let value = showInTonnes ? weight / 1000 : weight;
  let unit = showInTonnes ? (shortForm ? "t" : "tonnes") : "kg";

  let formattedValue = Intl.NumberFormat("sv-SE").format(
    showInTonnes ? value.toFixed(1) : Math.round(value),
  );

  return {
    weight: formattedValue,
    unit: unit,
    concat: `${formattedValue} ${unit}`,
  };
};

export const sum = (arr) =>
  Array.isArray(arr) ? arr.reduce((sum, x) => sum + x) : 0;
export const sumByKey = (arr, key) =>
  Array.isArray(arr) ? arr.reduce((a, b) => a + (b[key] || 0), 0) : 0;
export const percentOf = (value, total, fractionDigits = 1) =>
  value && total ? ((value / total) * 100).toFixed(fractionDigits) : 0;

export const objectLength = (obj) => {
  return typeof obj === "object" ? Object.keys(obj).length : 0;
};

export const formatDate = (d, f = "d MMM yyyy") =>
  !d ? null : format(parseISO(d), f, { locale: sv });
export const isValidDate = (d) => {
  return d instanceof Date && !isNaN(d);
};
export const ymdToDate = (d) =>
  !d ? null : new Date(d.year, d.month - 1, d.day);
export const dateToYmd = (d) =>
  !d
    ? null
    : { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };

export const uuid = () =>
  Math.random().toString(36).substring(2, 15) +
  Math.random().toString(36).substring(2, 15);

export const autoLinker = (content, target = "_BLANK") => {
  const urlRegex =
    /(\b(https?):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
  return content.replace(
    urlRegex,
    (match) => `<a target="${target}" href="${match}">${match}</a>`,
  );
};

export const formatNumber = (number) =>
  Number.isFinite(number) ? new Intl.NumberFormat().format(number) : 0;
export const formatEmissions = (emissions, decimals) => {
  if (!Number.isFinite(emissions)) {
    return 0.0;
  }

  if (Number.isInteger(emissions)) {
    // (emissions % 1 !== 0) {
    return emissions;
  }

  return (Math.round(emissions * 100) / 100).toFixed(decimals || 2);
};

export const countLateTasksByTeam = (team) => {
  let count = 0;
  for (let i = 0; i < team?.activities.length; i++) {
    for (let j = 0; j < team?.activities[i]?.entries.length; j++) {
      if (team?.activities[i].entries[j].is_late) {
        count = count + 1;
      }
    }
  }
  return count;
};

export const atomWithLocalStorage = (key, initialValue, expiry = null) => {
  const getInitialValue = () => {
    const item = localStorage.getItem(key);
    if (item !== null) {
      const data = JSON.parse(item);
      if (data?.expiry && data.expiry < Date.now()) {
        localStorage.removeItem(key);
        return initialValue;
      }
      return data?.value || false;
    }
    return initialValue;
  };
  const baseAtom = atom(getInitialValue());
  const derivedAtom = atom(
    (get) => get(baseAtom),
    (get, set, update) => {
      const nextValue =
        typeof update === "function" ? update(get(baseAtom)) : update;
      set(baseAtom, nextValue);
      localStorage.setItem(
        key,
        JSON.stringify({
          value: nextValue,
          expiry: expiry ? new Date().getTime() + expiry : null,
        }),
      );
    },
  );
  return derivedAtom;
};

export const calendarLocale = {
  // months list by order
  months: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],

  // week days by order
  weekDays: [
    {
      name: "Monday",
      short: "M",
    },
    {
      name: "Tuesday",
      short: "T",
    },
    {
      name: "Wednesday",
      short: "W",
    },
    {
      name: "Thursday",
      short: "T",
    },
    {
      name: "Friday",
      short: "F",
    },
    {
      name: "Saturday",
      short: "S",
      isWeekend: true,
    },
    {
      name: "Sunday",
      short: "S",
      isWeekend: true,
    },
  ],

  // just play around with this number between 0 and 6
  weekStartingIndex: 6,

  // return a { year: number, month: number, day: number } object
  getToday(gregorainTodayObject) {
    return gregorainTodayObject;
  },

  // return a native JavaScript date here
  toNativeDate(date) {
    return new Date(date.year, date.month - 1, date.day);
  },

  // return a number for date's month length
  getMonthLength(date) {
    return new Date(date.year, date.month, 0).getDate();
  },

  // return a transformed digit to your locale
  transformDigit(digit) {
    return digit;
  },

  // texts in the date picker
  nextMonth: "Next Month",
  previousMonth: "Previous Month",
  openMonthSelector: "Open Month Selector",
  openYearSelector: "Open Year Selector",
  closeMonthSelector: "Close Month Selector",
  closeYearSelector: "Close Year Selector",
  defaultPlaceholder: "Select...",

  // for input range value
  from: "from",
  to: "to",

  // used for input value when multi dates are selected
  digitSeparator: ",",

  // if your provide -2 for example, year will be 2 digited
  yearLetterSkip: 0,

  // is your language rtl or ltr?
  isRtl: false,
};

// used as default value for date range picker
export const generateDefaultDatespan = () => {
  return {
    // from: startOfMonth(subMonths(new Date(), 12)),
    // to: endOfMonth(subMonths(new Date(), 1))
    from: startOfYear(new Date()),
    to: endOfYear(new Date()),
  };
};

// linear percent reduction per year to reach a certain value
export const calculateYearlyPercentReduction = (oldValue, newValue, years) => {
  /**
   * Blir det konstigt detta när värdet man försöker nå är i princip 0?
   * skillnaden i procent blir ju väldigt stor beroende på om target är 1 eller 0.0001...
   * trots att skillnaden på detta för faktiska värdet är väldigt liten
   * kanske man behöver bygga in lite marginal?? på något sätt
   */
  return ((newValue / oldValue) ** (1 / years) - 1) * 100;
};

// TODO: Refactor this when remaking the budget graphs
export const refactorNeeded = (total, start, years) => {
  // console.log('total, start, years', total, start, years); // total, start, years 263421.815656139 28119.396325 27
  var mul = 1;
  const calculateBudget = (multiplier) => {
    var budget = total;
    var spend = start;
    for (var i = 1; i <= years; i++) {
      spend *= multiplier;
      budget -= spend;
      // console.log('i, spend, budget', i, spend, budget);
    }
    return budget > 0;
  };

  while (!calculateBudget(mul)) {
    mul -= 0.001;
  }

  // return mul;
  mul = (1 - mul) * 100;
  return -(Math.ceil(mul * 10) / 10);
};

export const percentageValueRecursively = (value, percentage, numberTimes) => {
  // console.log('numberTimes', numberTimes);
  if (numberTimes === 0) {
    return value;
  }

  return percentageValueRecursively(
    value - value * percentage,
    percentage,
    numberTimes - 1,
  );
};

export const rgb2hex = (rgb) => {
  if (!rgb) {
    return null;
  }
  if (rgb.indexOf("#") === 0) {
    return rgb;
  }
  return `#${rgb
    .match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/)
    .splice(1, 3)
    .map((v) => Number(v))
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("")}`;
};

export const trend = (x, y) => {
  let n = y.length;
  let sum_x = 0;
  let sum_y = 0;
  let sum_xy = 0;
  let sum_xx = 0;

  for (let i = 0; i < n; i++) {
    sum_x += x[i];
    sum_y += y[i];
    sum_xy += x[i] * y[i];
    sum_xx += x[i] * x[i];
  }

  let slope = (n * sum_xy - sum_x * sum_y) / (n * sum_xx - sum_x * sum_x);
  let intercept = (sum_y - slope * sum_x) / n;

  return { slope, intercept };
};

export const dateToIndex = (date) => {
  return `${date.getFullYear()}_${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}`;
};

export const copyToClipboard = async (text) => {
  // Navigator clipboard api needs a secure context (https)
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return true;
  } else {
    // Use the 'out of viewport hidden text area' trick
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Move textarea out of the viewport so it's not visible
    textArea.style.position = "absolute";
    textArea.style.left = "-999999px";

    document.body.prepend(textArea);
    textArea.select();

    try {
      document.execCommand("copy");
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      textArea.remove();
    }
  }

  return true;
};

export const showToast = (id, message, type = toast.TYPE.SUCCESS) => {
  toast.update(id, {
    render: message,
    type: type,
    isLoading: false,
    autoClose: 5000,
  });
};
